import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'lore',
  slug: 'lore-paris',
  label: 'Area Lore (Paris)',
  roll: {
    formula: 'Int 2 + Area Lore (Paris) 1 = 3 + die',
    niche: '+1 when Paris politics or power-play',
    altPairing: "Per 1 + 1 = 2 when noticing undercurrents or tension on the spot",
  },
  whenToUse: [
    'Recall which university scholar or influential family controls a particular quarter',
    'Navigate the competing interests between mendicant orders and the royal court',
    'Identify which tavern or street corner draws a specific crowd—merchants, clerics, soldiers',
    'Sense undercurrents in a crowded market or church gathering without asking directly',
  ],
  samples: [
    `GM: "Not-Quite-Free Real Estate: spring 1195 in Paris. A wine merchant from the Cité arrives at the covenant gates. You recognize his house sigil from tavern gossip during your winter there. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Elara: "Do you know him?"
You (Mikaela): "I know wine house reputations from that season. His mark is known but I couldn't tell you his actual dealings—just faces and sigils."
GM: "Roll Int 2 + Area Lore (Paris) 1 = 3 + die."

Teaches: Score 1 = shallow — she only knows headlines from her winter gossip, not deeper politics.`,
    `GM: "Spring 1195, Ex Astra covenant. Brother Alfric quotes a bishop's theological letter—you recognize the name from feuds that shaped Paris politics years ago. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Brother Alfric: "Should we be wary of this bishop's lands?"
You (Mikaela): "I mention what I heard that winter in Paris—enough to warn you we should avoid his territory. I know his reputation, not his theology."
GM: "Roll Int 2 + Area Lore (Paris) 1 = 3 + die (niche Politics = 4 to spot his agents on the road)."

Teaches: Her winter in Paris 1193 taught her gossip and obvious feuds, never doctrine.`,
    `GM: "Ex Astra spring 1195. A ragged mendicant friar approaches camp. His habit is worn, accent strange, but his order's badge is unclear."
Corvus: "Can you place him?"
You (Mikaela): "I studied mendicant orders' reputations when I sheltered in Paris—the speech and manner patterns. I try to figure out which order he serves."
GM: "Roll Int 2 + Area Lore (Paris) 1 = 3 + die."

Teaches: Score 1 means headlines—she knows which orders worked the Cité by rumor, not structure.`,
    `GM: "Not-Quite-Free Real Estate, spring 1195. Brother Alfric references a Parisian scholar's work on celestial geometry. You wonder if you crossed paths with that scholar during your one winter in the Cité. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Brother Alfric: "Did you meet him?"
You (Mikaela): "I recall hearing that name gossiped in merchant circles and cathedral cloisters. I try to place his reputation among Paris intellectuals, but it's only rumor."
GM: "Roll Int 2 + Area Lore (Paris) 1 = 3 + die."

Teaches: Curious +3 pulls her to know more, but score 1 caps what she recalls—names only.`,
    `GM: "Spring 1195, covenant contacts suggest a safe tavern in the Cité for a later errand. You sheltered in Paris one winter and know the district's basic geography. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Elara: "Is it trustworthy?"
You (Mikaela): "I remember passing that tavern on the way to the market—not my usual haunt, but I know where it sits and who keeps it. That much I can tell you."
GM: "Roll Int 2 + Area Lore (Paris) 1 = 3 + die (niche Politics = 4 if you read the keeper's loyalties)."

Teaches: Score 1 is the shallowest—landmarks and tavern signs only, a traveler's eye, not a native's map.`,
  ],
  vignette: "You walk the Île de la Cité as afternoon crowds thin. A bishop's livery catches your eye, and you recall that his house controls the south gate of the cathedral precinct. The knowledge settles uncomfortable—not lived experience, but gathered from whispered conversations in the Inn of the Fleur-de-Lys, where you sheltered one winter.",
  gotcha: "You know names and obvious landmarks, but only the most famous tales—your knowledge is thin, a traveller's impression rather than a scholar's map.",
};

export default demo;
